import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    Flex,
    ModalCloseButton,
    ModalBody,
    Button,
    Box,
    Text,
    Divider,
} from '@chakra-ui/react';
import { useMediaQuery } from 'react-responsive';
import { SCREEN_MD } from 'utils/constants';
import { ReactComponent as ArrowDownBlack } from 'assets/arrow-down-black-icon.svg';

export const styles = {
    ModalBackBtn: {
        cursor: 'pointer',
        transform: "rotate(90deg)",
        color: "#565656",
        marginTop: -3
    }
};

interface ThresholdData {
    threshold: number;
    signersCount: number;
}

interface SignerInfo {
    name: string;
    accountId: string;
}

interface OwnProps {
    data: {
        open: boolean;
        current: ThresholdData;
        next: ThresholdData;
        isLoading: boolean;
        create: () => void;
        handleClose: () => void;
        handleBack?: () => void;
        addedSigners?: SignerInfo[];
        removedSigners?: SignerInfo[];
        /** Signers whose account key changed on Hedera: this update puts the new key in the wallet */
        rotatedSigners?: { name?: string; accountId: string }[];
    };
}

const PlusIcon = () => (
    <Flex
        alignItems="center"
        justifyContent="center"
        w={6}
        h={6}
        borderRadius="full"
        bg="green.500"
        flexShrink={0}
    >
        <Text fontSize="14px" fontWeight="bold" color="white" lineHeight={1}>+</Text>
    </Flex>
);

const MinusIcon = () => (
    <Flex
        alignItems="center"
        justifyContent="center"
        w={6}
        h={6}
        borderRadius="full"
        bg="red.500"
        flexShrink={0}
    >
        <Text fontSize="14px" fontWeight="bold" color="white" lineHeight={1}>&ndash;</Text>
    </Flex>
);

const ThresholdSettingsModal = ({ data }: OwnProps) => {
    const { handleClose, handleBack, current, next, open, create, isLoading, addedSigners, removedSigners, rotatedSigners } = data;

    const isScreenMedium = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });

    const isThresholdOnly = !addedSigners?.length && !removedSigners?.length && !rotatedSigners?.length;

    return (
        <Modal onClose={handleClose} isOpen={open} isCentered>
            <ModalOverlay />
            <ModalContent
                maxWidth="586px"
                w="full"
                mx={isScreenMedium ? 4 : 0}
            >
                <ModalHeader pt={6} px={6} pb={0}>
                    <ArrowDownBlack
                        style={styles.ModalBackBtn}
                        onClick={() => {
                            if (handleBack) {
                                handleBack();
                            } else {
                                handleClose()
                            }
                        }}
                    />
                    <Flex alignItems="center" flexDirection="column">
                        <Text mt={10} size="2xl" variant="bodyBold">
                            Confirm MultiSig Update
                        </Text>
                    </Flex>
                    <ModalCloseButton
                        mt={2}
                        mr={2}
                        color="#565656"
                        _hover={{ border: 'none' }}
                        _focusVisible={{ outline: 'none' }}
                    />


                </ModalHeader>
                <ModalBody pb={14} pt={4} px={8} w="full" maxW={{ base: '420px', md: '520px' }} mx="auto">
                    <Flex alignItems="center" flexDirection="column" gap={6} flex={1}>
                        <Flex width="full" gap={8}>
                            <Box flex={1}>
                                <Text mb={2} size="sm" color="secondary" fontWeight="bold">
                                    Current Configuration
                                </Text>
                                <Text size="md" variant="bodyBold">
                                    {current.threshold}-of-{current.signersCount}
                                </Text>
                                <Text size="xs" color="gray.500">
                                    {current.signersCount} Signers
                                </Text>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <ArrowDownBlack style={{ transform: "rotate(-90deg)" }} />
                            </Box>
                            <Box flex={1}>
                                <Text mb={2} size="sm" color="secondary" fontWeight="bold">
                                    New Configuration
                                </Text>
                                <Text size="md" variant="bodyBold" color="green.500">
                                    {next.threshold}-of-{next.signersCount}
                                </Text>
                                <Text size="xs" color="gray.500">
                                    {next.signersCount} Signers
                                </Text>
                            </Box>
                        </Flex>

                        {addedSigners && addedSigners.length > 0 && (
                            <>
                                <Divider />
                                <Box width="full">
                                    <Text size="sm" color="secondary" fontWeight="bold" mb={3}>
                                        {addedSigners.length === 1 ? 'New Signer' : 'New Signers'}
                                    </Text>
                                    <Flex direction="column" gap={2}>
                                        {addedSigners.map((signer) => (
                                            <Flex
                                                key={signer.accountId}
                                                py={2}
                                                alignItems="center"
                                                gap={3}
                                            >
                                                <PlusIcon />
                                                <Box flex={1} minW={0}>
                                                    <Text size="sm" variant="bodyBold" isTruncated>
                                                        {signer.name || 'New Signer'}
                                                    </Text>
                                                    <Text size="xs" color="blackAlpha.500" fontFamily="mono">
                                                        {signer.accountId}
                                                    </Text>
                                                </Box>
                                            </Flex>
                                        ))}
                                    </Flex>
                                </Box>
                            </>
                        )}

                        {removedSigners && removedSigners.length > 0 && (
                            <>
                                <Divider />
                                <Box width="full">
                                    <Text size="sm" color="secondary" fontWeight="bold" mb={3}>
                                        {removedSigners.length === 1 ? 'Removed Signer' : 'Removed Signers'}
                                    </Text>
                                    <Flex direction="column" gap={2}>
                                        {removedSigners.map((signer) => (
                                            <Flex
                                                key={signer.accountId}
                                                py={2}
                                                alignItems="center"
                                                gap={3}
                                            >
                                                <MinusIcon />
                                                <Box flex={1} minW={0}>
                                                    <Text size="sm" variant="bodyBold" isTruncated>
                                                        {signer.name || 'Signer'}
                                                    </Text>
                                                    <Text size="xs" color="blackAlpha.500" fontFamily="mono">
                                                        {signer.accountId}
                                                    </Text>
                                                </Box>
                                            </Flex>
                                        ))}
                                    </Flex>
                                </Box>
                            </>
                        )}

                        {rotatedSigners && rotatedSigners.length > 0 && (
                            <>
                                <Divider />
                                <Box width="full">
                                    <Text size="sm" color="secondary" fontWeight="bold" mb={1}>
                                        Key Changes
                                    </Text>
                                    <Text size="xs" color="blackAlpha.600" mb={3}>
                                        {rotatedSigners.length === 1 ? 'This member has' : 'These members have'} changed
                                        their account key on Hedera. This update also replaces the old key in the wallet
                                        with the new one.
                                    </Text>
                                    <Flex direction="column" gap={2}>
                                        {rotatedSigners.map((signer) => (
                                            <Box key={signer.accountId} py={2}>
                                                <Text size="sm" variant="bodyBold" isTruncated>
                                                    {signer.name || 'Signer'}
                                                </Text>
                                                <Text size="xs" color="blackAlpha.500" fontFamily="mono">
                                                    {signer.accountId}
                                                </Text>
                                            </Box>
                                        ))}
                                    </Flex>
                                </Box>
                            </>
                        )}

                        {isThresholdOnly && (
                            <>
                                <Divider />
                                <Text size="xs" color="blackAlpha.500">
                                    Threshold change only — the signer list remains unchanged.
                                </Text>
                            </>
                        )}

                        <Divider />

                        <Button
                            w={204}
                            variant="primary"
                            borderRadius={6}
                            onClick={create}
                            isLoading={isLoading}
                        >
                            Confirm Update
                        </Button>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default ThresholdSettingsModal;
